import { useLayoutEffect, useRef, useState } from "react";

const clamp = (value, min, max) =>
  Math.min(Math.max(value, min), Math.max(min, max));

// Client-coordinate deltas + pointer capture work for mouse, touch and pen.
// Re-renders never reset the drag origin, and leaving the box never drops it.
export default function usePointerDrag({
  boundsRef,
  floating = false,
  visible = true,
  onActivate,
}) {
  const ref = useRef(null);
  const drag = useRef(null);
  const positionRef = useRef({ x: 12, y: 12 });
  const initialized = useRef(false);
  const [position, setPosition] = useState(positionRef.current);
  const [dragging, setDragging] = useState(false);

  const constrain = (next) => {
    const element = ref.current;
    const bounds = boundsRef?.current || element?.parentElement;
    const width = floating
      ? document.documentElement.clientWidth
      : bounds?.clientWidth || 0;
    const height = floating ? window.innerHeight : bounds?.clientHeight || 0;
    return {
      x: clamp(next.x, 12, width - (element?.offsetWidth || 96) - 12),
      y: clamp(next.y, 12, height - (element?.offsetHeight || 144) - 12),
    };
  };
  const move = (next) => {
    positionRef.current = constrain(next);
    setPosition(positionRef.current);
  };
  const cancel = () => {
    const current = drag.current;
    drag.current = null;
    if (current && ref.current?.hasPointerCapture(current.id))
      ref.current.releasePointerCapture(current.id);
    setDragging(false);
  };

  useLayoutEffect(() => {
    if (!visible || !ref.current) return;
    const resize = () => {
      cancel();
      if (!initialized.current) {
        // The first non-zero layout places the preview at the bottom-right.
        const bounds = boundsRef?.current || ref.current.parentElement;
        if (!floating && !bounds?.clientHeight) return;
        initialized.current = true;
        move({ x: Number.MAX_SAFE_INTEGER, y: Number.MAX_SAFE_INTEGER });
      } else move(positionRef.current);
    };
    resize();
    window.addEventListener("resize", resize);
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    observer?.observe(ref.current);
    if (boundsRef?.current) observer?.observe(boundsRef.current);
    return () => {
      window.removeEventListener("resize", resize);
      observer?.disconnect();
    };
    // Reinstall when a hidden preview is mounted again; not on every drag frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, floating, boundsRef]);

  const onPointerMove = (event) => {
    const current = drag.current;
    if (!current || event.pointerId !== current.id) return;
    const dx = event.clientX - current.clientX;
    const dy = event.clientY - current.clientY;
    if (Math.hypot(dx, dy) > 4) current.moved = true;
    if (current.moved) move({ x: current.x + dx, y: current.y + dy });
    event.preventDefault();
    event.stopPropagation();
  };

  return {
    ref,
    style: {
      transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
      cursor: dragging ? "grabbing" : "grab",
      touchAction: "none",
      userSelect: "none",
    },
    handlers: {
      onPointerDown: (event) => {
        if (
          !event.isPrimary ||
          event.button !== 0 ||
          event.target.closest("[data-no-drag]")
        )
          return;
        drag.current = {
          id: event.pointerId,
          clientX: event.clientX,
          clientY: event.clientY,
          ...positionRef.current,
          moved: false,
        };
        ref.current.setPointerCapture(event.pointerId);
        setDragging(true);
        event.stopPropagation();
      },
      onPointerMove,
      onPointerUp: (event) => {
        if (drag.current?.id !== event.pointerId) return;
        onPointerMove(event);
        const activate = !drag.current.moved;
        cancel();
        if (activate) onActivate?.();
      },
      onPointerCancel: cancel,
      onLostPointerCapture: cancel,
      onDragStart: (event) => event.preventDefault(),
    },
  };
}
